/**
 * EgoDeviceSetupView Component
 *
 * New device setup at /ego/setup?deviceId=UUID.
 * Checks if device is already known; if so, redirects to edit view.
 * Otherwise shows the config form with defaults.
 *
 * Requirements: 2.2, 2.4, 4.1–4.6
 */

import { useSearchParams, Navigate } from 'react-router-dom';
import { useEgoDevice } from '../hooks/useEgoDevice';
import { EgoConfigForm } from './EgoConfigForm';

export function EgoDeviceSetupView(): JSX.Element {
  const [searchParams] = useSearchParams();
  const deviceId = searchParams.get('deviceId');

  if (!deviceId) {
    return (
      <div className="ego-setup" style={{ padding: 32 }}>
        <p className="ego-form__error" role="alert">Device ID is required.</p>
      </div>
    );
  }

  return <EgoDeviceSetupContent deviceId={deviceId} />;
}

function EgoDeviceSetupContent({ deviceId }: { readonly deviceId: string }): JSX.Element {
  const { config, isLoading, isNotFound } = useEgoDevice(deviceId);

  if (isLoading) {
    return (
      <div className="ego-setup" style={{ padding: 32 }} role="status">
        <p>Loading…</p>
      </div>
    );
  }

  // Device is already known — redirect to edit view
  if (config && !isNotFound) {
    return <Navigate to={`/ego/${deviceId}`} replace />;
  }

  // Device is unknown — show setup form
  return (
    <div className="ego-setup" style={{ padding: 32 }}>
      <div className="ego-setup__banner" role="status">New Device</div>
      <p className="ego-setup__device-id" style={{ fontWeight: 600, fontSize: '1.125rem', marginBottom: 24 }}>
        {deviceId}
      </p>
      <EgoConfigForm deviceId={deviceId} mode="setup" />
    </div>
  );
}
